/**
 * Unified watch detection: decide, for one poll of one watch, whether
 * anything changed and what to deliver. Shared verbatim by the host's
 * per-agent watch tick and the API's group-watch runner so both targets have
 * identical change semantics.
 *
 * Pipeline (same order everywhere — runners AND the preview endpoint):
 *   tool output
 *     → optional selector projection
 *     → optional map JS  (pure: input.output → value | undefined)
 *     → trigger: declarative condition OR hash-on-change of the mapped value
 *
 * Map JS is stateless reshape only — no prev, no state, no emissions array.
 * "When to fire" is always host-owned (hash / condition). Return `undefined`
 * from the mapper to skip the poll entirely (no hash update, no wake).
 *
 * This module is platform-neutral: the QuickJS sandbox and sha256 are
 * INJECTED by the caller (host/api pass node-backed implementations; the
 * console never runs detection), keeping @cortex/types free of node builtins.
 */
import { z } from 'zod';
import { applyWatchSelector, parseWatchSelector, stableWatchStringify } from './watch-selector.js';
/**
 * Synthetic per-subject payload used by the group-watch runner when fanning
 * a single mapped value out through routing. Not produced by map JS anymore.
 */
export const WatchEmissionSchema = z.object({
    eventType: z.string().min(1),
    subject: z.object({ type: z.string().min(1), id: z.string().min(1) }),
    /** Human/agent-readable one-liner — becomes the wake-event content. */
    summary: z.string().min(1),
    /** Optional payload stored on the subject's fact lane. */
    data: z.unknown().optional(),
});
/**
 * Declarative gate on the (selector- then map-projected) value — no JS required.
 * Prefer this for "count > 0", "battery < 30", etc. Composes with map JS:
 * map to a thin `{ count }` then condition `gt 0`.
 *
 * `path` is optional and relative to the projected value (same selector grammar).
 * Empty path = the projected value itself.
 * `risingEdge` (default false): fire on every change while the predicate stays
 * true (ops clearance loops). Set true to fire only when it becomes true
 * (false→true). When false, fire whenever the predicate holds and the value
 * fingerprint changes.
 *
 * `matches` is the regex gate ("wake when the status text says hovering"):
 * `value` must be a compilable pattern (same convention as StateCheck/
 * eval-harness assertions). It runs host-side once per poll on the projected
 * scalar — no sandbox — so the pattern is length-capped; keep it simple
 * (no nested quantifiers) to avoid pathological backtracking.
 */
export const WatchConditionSchema = z
    .object({
    op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'matches']),
    value: z.union([z.number(), z.string(), z.boolean()]),
    path: z.string().min(1).max(200).optional(),
    risingEdge: z.boolean().default(false),
})
    .superRefine((condition, ctx) => {
    if (condition.op !== 'matches')
        return;
    if (typeof condition.value !== 'string' || condition.value.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'op "matches" requires a non-empty string regex value',
        });
        return;
    }
    if (condition.value.length > 500) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'op "matches" regex is capped at 500 chars',
        });
        return;
    }
    try {
        new RegExp(condition.value);
    }
    catch {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `op "matches" value is not a valid regex: ${condition.value}`,
        });
    }
});
/**
 * Project `output` through a selector, or return it untouched. Throws on
 * invalid selector grammar — the API validates selectors on write, so a
 * throw here means a row was corrupted, which SHOULD surface as a poll error.
 */
export function projectWatchOutput(output, selector) {
    if (!selector)
        return output;
    const segments = parseWatchSelector(selector);
    if (!segments)
        throw new Error(`Invalid watch selector on stored row: ${selector}`);
    return applyWatchSelector(output, segments);
}
/**
 * Coerce a raw mapper return value.
 * - `undefined` / `null` → skip poll (`skipped: true`)
 * - anything else (including `false`, `0`, `""`, `[]`, `{}`) → the projected payload
 *
 * Deliberately does NOT accept the legacy `{ emissions, state }` object as a
 * special protocol — that object would be treated as a normal mapped payload
 * (and would hash-stable loop). Callers should migrate maps to return the
 * value directly.
 */
export function coerceMapperResult(raw) {
    if (raw === undefined || raw === null)
        return { skipped: true };
    return { skipped: false, projected: raw };
}
/**
 * Run one watch's detection for one poll. Callers persist `hash` from the
 * result as the next poll's `lastHash` cursor.
 */
export async function detectWatchChange(args, deps) {
    let projected = projectWatchOutput(args.output, args.selector);
    if (args.code) {
        const raw = await deps.exec({
            code: args.code,
            input: { output: projected },
            bindings: args.bindings ?? undefined,
            timeoutMs: args.timeoutMs ?? 5000,
            nodeKey: args.nodeKey,
        });
        const mapped = coerceMapperResult(raw);
        if (mapped.skipped) {
            // Skip poll entirely — do not advance the hash cursor.
            return { kind: 'unchanged', projected: undefined };
        }
        projected = mapped.projected;
    }
    if (args.condition) {
        const gated = detectConditionChange(projected, args.condition, args.lastHash ?? null, deps.sha256);
        // Always surface the mapped/projected value for preview + debugging.
        return { ...gated, projected };
    }
    const hash = deps.sha256(stableWatchStringify(projected));
    if (hash === args.lastHash)
        return { kind: 'unchanged', hash, projected };
    return { kind: 'hash', hash, projected };
}
/**
 * Resolve a comparable scalar from the selector projection (+ optional path).
 *
 * Path is relative to the projection, NOT the raw tool output — the selector
 * (and optional map) already ran. Operators often paste the same field into
 * both boxes (`selector=events_active`, `path=events_active`); applying the
 * path again to a scalar yields `undefined` and the condition never fires.
 * When the path misses on a scalar/array projection, fall back to the
 * projection itself.
 */
export function extractWatchConditionValue(projected, path) {
    if (!path)
        return projected;
    const segments = parseWatchSelector(path);
    if (!segments)
        throw new Error(`Invalid condition path: ${path}`);
    const got = applyWatchSelector(projected, segments);
    if (got === undefined &&
        (typeof projected === 'number' ||
            typeof projected === 'string' ||
            typeof projected === 'boolean' ||
            Array.isArray(projected))) {
        return projected;
    }
    return got;
}
export function evaluateWatchCondition(raw, condition) {
    // Regex gate: match the RAW scalar's string form. coerceComparable would
    // number-ify numeric-looking strings ("007" → 7) and corrupt the pattern's
    // subject, and arrays/objects have no meaningful string form here.
    if (condition.op === 'matches') {
        if (typeof condition.value !== 'string')
            return false;
        if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean') {
            return false;
        }
        try {
            return new RegExp(condition.value).test(String(raw));
        }
        catch {
            return false; // schema rejects bad patterns on write; stay safe on corrupt rows
        }
    }
    const left = coerceComparable(raw);
    const right = coerceComparable(condition.value);
    if (left === undefined || right === undefined)
        return false;
    switch (condition.op) {
        case 'gt':
            return typeof left === 'number' && typeof right === 'number' && left > right;
        case 'gte':
            return typeof left === 'number' && typeof right === 'number' && left >= right;
        case 'lt':
            return typeof left === 'number' && typeof right === 'number' && left < right;
        case 'lte':
            return typeof left === 'number' && typeof right === 'number' && left <= right;
        case 'eq':
            return left === right;
        case 'neq':
            return left !== right;
        default:
            return false;
    }
}
function coerceComparable(v) {
    if (typeof v === 'number' && Number.isFinite(v))
        return v;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'string') {
        const n = Number(v);
        if (v.trim() !== '' && Number.isFinite(n))
            return n;
        return v;
    }
    // Arrays: use length (open events list → count)
    if (Array.isArray(v))
        return v.length;
    // Common envelopes
    if (v && typeof v === 'object') {
        const o = v;
        if (typeof o.count === 'number')
            return o.count;
        if (typeof o.length === 'number')
            return o.length;
        if (typeof o.value === 'number')
            return o.value;
    }
    return undefined;
}
function detectConditionChange(projected, condition, lastHash, sha256) {
    const extracted = extractWatchConditionValue(projected, condition.path);
    const met = evaluateWatchCondition(extracted, condition);
    const rising = condition.risingEdge === true;
    const valueFp = sha256(stableWatchStringify(extracted)).slice(0, 16);
    // Cursor encodes met + value so rising-edge and value-change modes both work.
    const cursor = `cond:${met ? '1' : '0'}:${valueFp}`;
    if (lastHash === cursor)
        return { kind: 'unchanged', hash: cursor };
    if (rising) {
        const wasMet = lastHash?.startsWith('cond:1:') === true;
        if (!met)
            return { kind: 'unchanged', hash: cursor };
        if (wasMet)
            return { kind: 'unchanged', hash: cursor }; // still true — no re-fire
        // false→true (or first poll met with no prior true)
        return { kind: 'hash', hash: cursor, projected };
    }
    // Level mode: only fire while met AND value fingerprint moved.
    if (!met)
        return { kind: 'unchanged', hash: cursor };
    return { kind: 'hash', hash: cursor, projected };
}
/** Build wake event text: optional operator prompt + compact lane snapshot. */
export function formatWatchWakeContent(args) {
    const max = args.maxChars ?? 800;
    const header = args.wakePrompt?.trim() || `Watch "${args.watchName}" updated lane "${args.laneId}".`;
    let snapshot;
    try {
        if (Array.isArray(args.projected)) {
            snapshot = `count=${args.projected.length} ${stableWatchStringify(args.projected)}`;
        }
        else if (typeof args.projected === 'number' || typeof args.projected === 'boolean') {
            snapshot = String(args.projected);
        }
        else {
            snapshot = stableWatchStringify(args.projected);
        }
    }
    catch {
        snapshot = String(args.projected);
    }
    if (snapshot.length > max)
        snapshot = snapshot.slice(0, max) + '…';
    return header + '\n\nlane ' + args.laneId + ': ' + snapshot;
}
/** Lane one emission writes for a per-agent watch: `<laneId>_<subject.type>_<subject.id>`. */
export function watchEmissionLaneId(laneId, emission) {
    return `${laneId}_${emission.subject.type}_${emission.subject.id}`;
}
/**
 * Deterministic id for a group-watch emission (dedupe key for routing ingest).
 * Stable across identical subject+type+summary so retries don't double-fan-out.
 */
export function watchEmissionEventId(watchId, emission, sha256) {
    return sha256(stableWatchStringify({
        watchId,
        eventType: emission.eventType,
        subject: emission.subject,
        summary: emission.summary,
    })).slice(0, 32);
}
/** Re-export so runners don't import the selector module just for stringify. */
export { stableWatchStringify } from './watch-selector.js';
//# sourceMappingURL=watch-detection.js.map